import { useEffect, useState } from "react";
import { toast } from "sonner";
import { IdentificationBadge, Plus } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

export default function BizStaff() {
  const [list, setList] = useState(null);
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", password: "", role: "cashier", branch_id: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/business/staff").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { load(); api.get("/business/branches").then((r) => setBranches(r.data)).catch(() => {}); }, []);

  const invite = async () => {
    setSaving(true);
    try {
      await api.post("/business/staff", { ...f, branch_id: f.branch_id || null });
      toast.success("Staff invited");
      setOpen(false); setF({ name: "", email: "", password: "", role: "cashier", branch_id: "" }); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const toggle = async (s) => {
    try { await api.put(`/business/staff/${s.id}`, { status: s.status === "active" ? "inactive" : "active" }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!list) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="font-display font-bold text-3xl flex items-center gap-2"><IdentificationBadge size={26} className="text-primary" /> Staff</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="invite-staff-btn" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"><Plus size={18} className="mr-1" /> Invite staff</Button></DialogTrigger>
          <DialogContent className="bg-card border-black/10">
            <DialogHeader><DialogTitle className="font-display">Invite staff</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs text-muted-foreground">Name</Label><Input data-testid="staff-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1.5 bg-muted/40 border-black/10 h-11 rounded-xl" /></div>
              <div><Label className="text-xs text-muted-foreground">Email</Label><Input data-testid="staff-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="mt-1.5 bg-muted/40 border-black/10 h-11 rounded-xl" /></div>
              <div><Label className="text-xs text-muted-foreground">Temporary password</Label><Input data-testid="staff-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="mt-1.5 bg-muted/40 border-black/10 h-11 rounded-xl" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-muted-foreground">Role</Label>
                  <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}><SelectTrigger data-testid="staff-role" className="mt-1.5 bg-muted/40 border-black/10 h-11 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="cashier">Cashier</SelectItem><SelectItem value="business_manager">Manager</SelectItem></SelectContent></Select>
                </div>
                <div><Label className="text-xs text-muted-foreground">Branch</Label>
                  <Select value={f.branch_id} onValueChange={(v) => setF({ ...f, branch_id: v })}><SelectTrigger data-testid="staff-branch" className="mt-1.5 bg-muted/40 border-black/10 h-11 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent></Select>
                </div>
              </div>
              <Button data-testid="save-staff" onClick={invite} disabled={saving} className="w-full h-11 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">{saving ? "Inviting…" : "Send invite"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="card-surface overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-muted-foreground text-xs uppercase tracking-wider border-b border-black/8"><tr><th className="text-left p-4">Name</th><th className="text-left p-4">Email</th><th className="text-left p-4">Role</th><th className="text-right p-4">Status</th></tr></thead>
          <tbody>
            {list.map((s) => (
              <tr key={s.id} data-testid="staff-row" className="border-b border-black/5">
                <td className="p-4">{s.name}</td>
                <td className="p-4 text-muted-foreground">{s.email}</td>
                <td className="p-4 capitalize">{s.role.replace("_", " ")}</td>
                <td className="p-4 text-right">
                  {s.role === "business_owner" ? <span className="text-primary text-xs">Owner</span> :
                    <div className="flex items-center gap-1.5 justify-end"><Switch checked={s.status === "active"} onCheckedChange={() => toggle(s)} data-testid="toggle-staff" /><span className="text-xs text-muted-foreground">{s.status}</span></div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
